<script setup>
import {useChatStore} from "@/stores/chat.js";
import {computed, onMounted, onUnmounted, ref} from "vue";
import Badge from "@/components/Badge/index.vue";
import {useFormattedNearestTime} from "@/composables/useFormattedNearestTime.js";
import {Message, VirtualScroller} from "primevue";

const {getFormattedDate} = useFormattedNearestTime()
const chatStore = useChatStore();
const contacts = computed(() => chatStore.get_all_contacts);
let intervalId = null;
const cleared = ref(false)
const FOUR_HOURS = 4 * 60 * 60 * 1000;

const getBadgeClass = (item) => {
  const baseClass = "text-white !font-normal !text-xs bg-gray-600 !px-2";
  const bgClass = {
    Account: "!bg-[#2196F3]",
    Boost: "!bg-[#4CAF50]",
    Currency: "!bg-[#FF6000]",
    Item: "!bg-[#795548]",
    "Gift Card": "!bg-[#E91E63]",
  }[item.orderType] || "";

  return `${baseClass} ${bgClass}`;
};

async function fetchNewContacts() {
  if (cleared.value) return;
  if (!chatStore.isGettingNewContacts) {
    await chatStore.action_get_new_contacts();
  }
  if (!cleared.value) {
    intervalId = setTimeout(fetchNewContacts, 2000);
  }
}

onMounted(() => {
  chatStore.action_get_all_contacts()
  fetchNewContacts();
})

onUnmounted(() => {
  //console.log('unmounted')
  if (intervalId) {
    clearInterval(intervalId)
    cleared.value = true
  }
})
</script>

<template>
  <div class="flex flex-col divide-y divide-slate-100 dark:divide-slate-700 h-full overflow-x-hidden">
    <div v-if="chatStore.searchContactError" class="p-1">
      <Message severity="error">{{ chatStore.searchContactError }}</Message>
    </div>
    <VirtualScroller :items="contacts" :itemSize="78" class="!h-full !w-full overflow-x-hidden custom-scrollbar"
                     :pt="{content:{class: 'overflow-x-hidden w-full divide-y divide-slate-100 dark:divide-slate-700'}}">
      <template v-slot:item="{ item, options }">
        <div
            @click="chatStore.openChat(item)"
            class="relative block w-full py-1 focus:ring-0 outline-none cursor-pointer group transition-all duration-150 hover:-translate-y-1"
            :class="{'bg-slate-200 dark:bg-slate-700 dark:bg-opacity-70' : chatStore.selectedChat.channel === item.channel,
            'bg-[linear-gradient(to_right,_#EE097950,_#FF6A0050)]' : item.contact?.startsWith('G2gAdmin')}"
        >
          <div v-if="item.contact?.startsWith('G2gAdmin')"
               class="absolute w-full h-full inset-0 bg-right bg-contain bg-no-repeat opacity-20 bg-[url(@/assets/images/admin.png)]"
          ></div>
          <div class="px-2">
            <div
                :class="`flex flex-col gap-1 dir-ltr text-left border-l-4 pl-2 ${item.store === 'DealHub USD' || item.store === 'DealHub' ? 'border-[#F5971D]':''} ${item.store === 'Incepto USD' || item.store === 'Incepto' ? 'border-[#43D9AA]':''}`">
              <div class="flex flex-row justify-between gap-4">
                <span
                    class="select-text cursor-text text-slate-800 dark:text-slate-300 text-sm font-medium overflow-hidden whitespace-nowrap text-ellipsis">
                  {{ item.contact || "-" }}
                </span>
                <div class="block text-xs text-slate-400 dark:text-slate-400 font-normal relative top-1">
                  {{ getFormattedDate(item.lastMessageTime) }}
                </div>
              </div>
              <div class="flex flex-row gap-2 items-center">
                <Badge :badge-class="getBadgeClass(item)">
                  <span class="block overflow-hidden whitespace-nowrap text-ellipsis max-w-20">{{
                      item.orderType
                    }}</span>
                </Badge>
                <span v-if="item.orderGame" class="text-xs text-slate-300">{{ item.orderGame }}</span>
                <span v-if="item.isUrgent && (Date.now() - (item.customizedTime || 0) > FOUR_HOURS)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                    <path fill="yellow"
                          d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575ZM8 5a.75.75 0 0 0-.75.75v2.5a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8 5m1 6a1 1 0 1 0-2 0a1 1 0 0 0 2 0"/>
                  </svg>
                </span>
              </div>
              <div class="flex flex-row gap-4 items-center">
                <div
                    class="flex-1 block text-xs text-slate-400 dark:text-slate-400 font-normal overflow-hidden whitespace-nowrap text-ellipsis">
                  <template v-if="item.isTyping">
                    <span class="text-blue-400">{{ item.contact }}</span>
                    <span> is typing ...</span>
                  </template>
                  <template v-else>
                    <span class="text-blue-400">
                      {{
                        item.lastMessageFromID ? (item.lastMessageFromID === '1001234905' || item.lastMessageFromID === '1000670527')
                                ? 'You: '
                                : ''
                            : 'System: '
                      }}
                    </span>
                    <span>{{ item.lastMessage }}</span>
                  </template>
                </div>
                <div class="flex flex-row gap-1 items-end relative bottom-1">
                  <span v-if="item.isUrgent" class="text-[18px] leading-none">
                    {{ item.urgencyEmoji }}
                  </span>
                  <div v-if="item.unreadMessages > 0"
                       class="size-5 text-[12px] bg-red-700 text-white rounded-full flex flex-col items-center justify-center">
                    <p class="h-auto">{{ item.unreadMessages }}</p>
                  </div>
                  <div
                      v-else-if="item.lastMessageFromID && !(item.lastMessageFromID === '1001234905') && !(item.lastMessageFromID === '1000670527')"
                      class="size-5 bg-red-700 text-white rounded-full flex flex-col items-center justify-center"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </VirtualScroller>
  </div>
</template>

<style scoped lang="scss">

</style>